import type { PublicInterviewInfo } from '@eduvy/core';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Spinner } from '../components/ui';
import { BuiltinRoom } from './BuiltinRoom';
import { EndScreen } from './livekit/EndScreen';

// livekit-client is large: load it only for LiveKit interviews, never on the recruiter pages.
const LiveKitInterview = lazy(() => import('./livekit/LiveKitInterview').then((m) => ({ default: m.LiveKitInterview })));

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; info: PublicInterviewInfo }
  | { kind: 'done'; title: string; body: string }
  | { kind: 'error'; message: string };

/**
 * The candidate's interview page (/i/:token). Loads the link's landing data, then hands over to
 * the LiveKit room (voice or video) or the built-in WebSocket room, whichever the server runs.
 */
export function InterviewRoom() {
  const { token } = useParams() as { token: string };
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/public/interview/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = (await r.json().catch(() => ({}))) as PublicInterviewInfo & { error?: string };
        if (!r.ok) throw new Error(body.error ?? 'This link is not valid.');
        if (cancelled) return;
        if (body.status === 'completed')
          setLoad({ kind: 'done', title: 'This interview has already been completed.', body: 'Thank you! You can close this window.' });
        else if (body.status === 'expired')
          setLoad({ kind: 'error', message: 'This interview link has expired. Please contact the hiring team for a new one.' });
        else setLoad({ kind: 'ready', info: body });
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoad({ kind: 'error', message: err instanceof Error ? err.message : 'This link is not valid.' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  switch (load.kind) {
    case 'loading':
      return <Loading />;
    case 'done':
      return <EndScreen tone="done" title={load.title} body={load.body} />;
    case 'error':
      return <EndScreen tone="warn" title={load.message} />;
    case 'ready':
      return load.info.runtime === 'livekit' ? (
        <Suspense fallback={<Loading />}>
          <LiveKitInterview token={token} info={load.info} />
        </Suspense>
      ) : (
        <BuiltinRoom token={token} info={load.info} />
      );
  }
}

function Loading() {
  return (
    <div className="flex h-full items-center justify-center bg-[#f6f4fb] text-[#605e6c]" role="status" aria-label="Loading your interview">
      <Spinner />
    </div>
  );
}
